/**
 * Dev-only hook that logs Herald spec summary after updates **settle** (trailing debounce).
 * Does **not** throw — validation and sanitize notices use `console` only (never `throw`).
 * No-op in production.
 *
 * Debounce: waits **idle time** after the last spec change (default 1500ms) before logging, so
 * streaming / rapid updates do not spam the console. Identical raw+sanitized snapshots are skipped.
 *
 * Toggle in dev: `window.__HERALD_SPEC_DEBUG__ = false` to disable; `true` to enable (default on).
 * Optional: `window.__HERALD_SPEC_DEBUG_DELAY_MS__ = 2000` to change the idle delay (milliseconds).
 *
 * Tool calls (raw states, input/output): in dev only, `window.__HERALD_DEBUG_TOOL_CALLS__ = true`
 * shows ChatToolCallDisplay rows. Omitted from production bundles (lazy chunk + `import.meta.env.DEV`).
 *
 * Console helpers (after a spec has been logged):
 * - `window.__downloadHeraldSpecTxt__()` — sanitized spec as aligned .txt
 * - `window.__copyHeraldSpecJson__()` — sanitized spec JSON on the clipboard
 * - `window.__lastHeraldSpec__` — last sanitized summary; `__lastHeraldSpecRaw__` — model payload before sanitize
 * - `window.__lastHeraldSpecFull__` — last rendered `Spec` (for JSON copy, includes `state` when present)
 */
import { type Spec, validateSpec } from "@json-render/core";
import { useEffect, useRef } from "react";

import { findStrippedChildRefs } from "@/lib/chat-sanitize/sanitizeSpecRefs";

/** Default idle time (ms) after the last spec change before one console log. */
const DEFAULT_LOG_DELAY_MS = 1500;

declare global {
  interface Window {
    __HERALD_SPEC_DEBUG__?: boolean;
    /** Override idle delay (ms) before logging after the last spec update. */
    __HERALD_SPEC_DEBUG_DELAY_MS__?: number;
    __lastHeraldSpec__?: SpecSummaryInput | null;
    __lastHeraldSpecRaw__?: SpecSummaryInput | null;
    __lastHeraldSpecFull__?: Spec | null;
    __downloadHeraldSpecTxt__?: () => void;
    __copyHeraldSpecJson__?: () => void;
    /**
     * Dev only: when `true`, assistant messages render expandable tool-call rows (see AssistantMessageContent).
     * No effect in production.
     */
    __HERALD_DEBUG_TOOL_CALLS__?: boolean;
  }
}

interface SpecSummaryInput {
  root: string;
  elements: Record<string, { type?: string; children?: string[] }>;
}

export interface HeraldSpecDevToolsInput {
  /** Model / transport payload (may include dangling child ids). */
  raw: Spec | null;
  /** After sanitizeSpecRefs only; used for {@link findStrippedChildRefs} (not legacy migration). */
  sanitized: Spec | null;
  /** Spec passed to Renderer (sanitize + legacy migration when applicable). */
  rendered?: Spec | null;
}

const specToTxt = (spec: SpecSummaryInput): string => {
  const lines = [`root: ${spec.root}`, ""];
  const entries = Object.entries(spec.elements);
  const col = (s: string, w: number) => s.padEnd(w);
  const maxKey = Math.max(8, ...entries.map(([k]) => k.length));
  const maxType = Math.max(
    6,
    ...entries.map(([, el]) => (el?.type ?? "?").length),
  );
  lines.push(
    `${col("key", maxKey)}  ${col("type", maxType)}  childCount  children`,
  );
  lines.push("-".repeat(maxKey + maxType + 20));
  for (const [key, el] of entries) {
    const children = (el?.children ?? []).join(", ") || "—";
    lines.push(
      `${col(key, maxKey)}  ${col(el?.type ?? "?", maxType)}  ${String(el?.children?.length ?? 0).padStart(10)}  ${children}`,
    );
  }
  return lines.join("\n");
};

/** Indented tree from root for quick structure scanning in the console. */
const buildSpecTreeLines = (
  rootId: string,
  elements: SpecSummaryInput["elements"],
): string => {
  const lines: string[] = [];
  const visit = (id: string, depth: number, seenPath: Set<string>) => {
    const pad = "  ".repeat(depth);
    if (seenPath.has(id)) {
      lines.push(`${pad}${id} (cycle)`);
      return;
    }
    const el = elements[id];
    if (!el) {
      lines.push(`${pad}⚠ ${id} (missing from elements)`);
      return;
    }
    lines.push(`${pad}${id} · ${el.type ?? "?"}`);
    const nextSeen = new Set(seenPath).add(id);
    for (const cid of el.children ?? []) {
      visit(cid, depth + 1, nextSeen);
    }
  };
  visit(rootId, 0, new Set());
  return lines.join("\n");
};

const toSummary = (spec: Spec | null): SpecSummaryInput | null => {
  if (!spec?.root || !spec.elements) return null;
  return {
    root: spec.root,
    elements: spec.elements as SpecSummaryInput["elements"],
  };
};

const logSpecSummary = (input: HeraldSpecDevToolsInput) => {
  const { raw, sanitized, rendered } = input;
  const displaySpec = rendered ?? sanitized;
  const summary = toSummary(displaySpec);
  if (!summary) return;

  const entries = Object.entries(summary.elements);
  const tableRows = entries.map(([key, el]) => ({
    key,
    type: el?.type ?? "?",
    childCount: el?.children?.length ?? 0,
    children: (el?.children ?? []).join(", ") || "—",
  }));

  window.__lastHeraldSpec__ = summary;
  window.__lastHeraldSpecRaw__ = toSummary(raw);
  window.__lastHeraldSpecFull__ = displaySpec;

  const stripped = findStrippedChildRefs(raw, sanitized);
  const toValidate = raw ?? sanitized;
  const validation =
    toValidate?.root && toValidate.elements
      ? validateSpec(toValidate, { checkOrphans: true })
      : null;

  const label = `[HeraldSpecRenderer] spec (root: ${summary.root}, ${entries.length} elements${stripped.length ? `, ${stripped.length} parent(s) had stripped refs` : ""})`;
  console.groupCollapsed(label);
  console.log("root:", summary.root);
  if (stripped.length > 0) {
    console.warn(
      "[sanitizeSpecRefs] Removed undefined child ids (not in elements):",
      stripped,
    );
  }
  if (validation && validation.issues.length > 0) {
    console.warn(
      `[validateSpec] ${validation.issues.length} issue(s) (console only — does not throw)`,
    );
    console.table(
      validation.issues.map((i) => ({
        severity: i.severity,
        code: i.code,
        elementKey: i.elementKey ?? "—",
        message: i.message,
      })),
    );
  } else if (validation) {
    console.log("[validateSpec] structural OK (no issues)");
  }
  console.log("Tree (rendered):");
  console.log(buildSpecTreeLines(summary.root, summary.elements));
  console.table(tableRows);
  console.log(
    "Helpers: window.__downloadHeraldSpecTxt__() · window.__copyHeraldSpecJson__()",
  );
  console.groupEnd();
};

/**
 * Logs spec structure in dev after updates settle (debounced). Disable with window.__HERALD_SPEC_DEBUG__ = false.
 */
const specSnapshotFingerprint = (input: HeraldSpecDevToolsInput): string =>
  JSON.stringify({
    raw: input.raw,
    sanitized: input.sanitized,
    rendered: input.rendered,
  });

const getLogDelayMs = (): number => {
  const n = window.__HERALD_SPEC_DEBUG_DELAY_MS__;
  return typeof n === "number" && n >= 0 && Number.isFinite(n)
    ? n
    : DEFAULT_LOG_DELAY_MS;
};

/**
 * Dev-only: debounced console logging and window helpers for json-render spec debugging.
 */
export const useHeraldSpecDevTools = (input: HeraldSpecDevToolsInput) => {
  const inputRef = useRef<HeraldSpecDevToolsInput>(input);
  inputRef.current = input;
  const lastLoggedFingerprintRef = useRef<string | null>(null);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    window.__downloadHeraldSpecTxt__ = () => {
      const spec = window.__lastHeraldSpec__;
      if (!spec?.root || !spec?.elements) {
        console.warn(
          "[HeraldSpecRenderer] No spec to download. Wait for a spec to be logged first.",
        );
        return;
      }
      const txt = specToTxt(spec);
      const blob = new Blob([txt], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `herald-spec-${new Date().toISOString().replace(/[:.]/g, "-")}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    };
    window.__copyHeraldSpecJson__ = async () => {
      const full = window.__lastHeraldSpecFull__;
      if (!full?.root || !full?.elements) {
        console.warn(
          "[HeraldSpecRenderer] No spec to copy. Wait for a spec to be logged first.",
        );
        return;
      }
      try {
        await navigator.clipboard.writeText(JSON.stringify(full, null, 2));
        console.log(
          "[HeraldSpecRenderer] Full rendered spec JSON copied to clipboard.",
        );
      } catch (e) {
        console.error("[HeraldSpecRenderer] Clipboard copy failed:", e);
      }
    };
    return () => {
      window.__downloadHeraldSpecTxt__ = undefined;
      window.__copyHeraldSpecJson__ = undefined;
      // Intentionally keep __lastHeraldSpec__, __lastHeraldSpecRaw__, and __lastHeraldSpecFull__
      // on window after unmount so the console can still inspect the last logged spec when navigating away.
    };
  }, []);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (window.__HERALD_SPEC_DEBUG__ === false) return;
    const displaySpec = input.rendered ?? input.sanitized;
    if (!displaySpec?.root || !displaySpec?.elements) return;

    const delayMs = getLogDelayMs();
    const id = window.setTimeout(() => {
      const latest = inputRef.current;
      const latestDisplay = latest.rendered ?? latest.sanitized;
      if (!latestDisplay?.root || !latestDisplay?.elements) return;

      const fp = specSnapshotFingerprint(latest);
      if (fp === lastLoggedFingerprintRef.current) return;
      lastLoggedFingerprintRef.current = fp;

      logSpecSummary(latest);
    }, delayMs);

    return () => window.clearTimeout(id);
  }, [input.raw, input.sanitized, input.rendered]);
};
